import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Outlet } from 'react-router-dom';

import {
  isValidCadenceSettings,
  runCadence,
  type CadenceProgress,
  type CadenceSettings,
} from './cadence-runner.js';
import { useQuickCreate } from './quick-create.js';

export type BulkCreateCadenceStatus = 'idle' | 'running' | 'stopped' | 'completed' | 'failed';

export type BulkCreateCadenceController = {
  status: BulkCreateCadenceStatus;
  runSettings: CadenceSettings | null;
  progress: CadenceProgress | null;
  start: (settings: CadenceSettings) => boolean;
  stop: () => void;
};

type ActiveRun = {
  abortController: AbortController;
};

const BulkCreateCadenceContext = createContext<BulkCreateCadenceController | null>(null);

export function useBulkCreateCadence(): BulkCreateCadenceController {
  const controller = useContext(BulkCreateCadenceContext);
  if (!controller) {
    throw new Error('Bulk Create cadence must be used inside BulkCreateCadenceBoundary.');
  }
  return controller;
}

export function BulkCreateCadenceBoundary() {
  const quickCreate = useQuickCreate();
  const quickCreateRef = useRef(quickCreate);
  const activeRunRef = useRef<ActiveRun | null>(null);
  const attemptInFlightRef = useRef(false);
  const [status, setStatus] = useState<BulkCreateCadenceStatus>('idle');
  const [runSettings, setRunSettings] = useState<CadenceSettings | null>(null);
  const [progress, setProgress] = useState<CadenceProgress | null>(null);

  quickCreateRef.current = quickCreate;

  useEffect(
    () => () => {
      activeRunRef.current?.abortController.abort();
      activeRunRef.current = null;
    },
    [],
  );

  const start = useCallback((settings: CadenceSettings) => {
    if (activeRunRef.current || !isValidCadenceSettings(settings)) return false;

    const snapshot = { ...settings };
    const abortController = new AbortController();
    const activeRun = { abortController };
    activeRunRef.current = activeRun;
    setRunSettings(snapshot);
    setProgress(null);
    setStatus('running');

    void runCadence({
      settings: snapshot,
      signal: abortController.signal,
      onProgress: setProgress,
      attempt: () => {
        const currentQuickCreate = quickCreateRef.current;
        if (
          !currentQuickCreate.available ||
          currentQuickCreate.isPending ||
          attemptInFlightRef.current
        ) {
          return;
        }

        attemptInFlightRef.current = true;
        void currentQuickCreate
          .run()
          .catch(() => undefined)
          .finally(() => {
            attemptInFlightRef.current = false;
          });
      },
    })
      .then((result) => {
        if (activeRunRef.current !== activeRun) return;
        activeRunRef.current = null;
        setStatus(result === 'completed' ? 'completed' : 'stopped');
      })
      .catch(() => {
        if (activeRunRef.current !== activeRun) return;
        activeRunRef.current = null;
        setStatus('failed');
      });

    return true;
  }, []);

  const stop = useCallback(() => {
    activeRunRef.current?.abortController.abort();
  }, []);

  const controller = useMemo(
    () => ({ status, runSettings, progress, start, stop }),
    [progress, runSettings, start, status, stop],
  );

  return (
    <BulkCreateCadenceContext.Provider value={controller}>
      <Outlet />
    </BulkCreateCadenceContext.Provider>
  );
}
